import React, { useMemo, useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  ButtonBase,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Rating,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import TodayIcon from "@mui/icons-material/Today";
import StarRateIcon from "@mui/icons-material/StarRate";
import ClearIcon from "@mui/icons-material/Clear";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";

import { useAuth } from "../context/authContext";
import { useFollowingForContent } from "../hooks/useCustomQuery";
import { getStatusOptions, getTimeAgo } from "../utils/utils";
import FriendJournalEntry from "../features/track/FriendJournalEntry";
import HoverRating from "./HoverRating";
import { AVATAR_SIZE } from "../features/track/journalEntryLayout";

// Finished and queued share the top row with the average rating; started and
// stopped are rarer, so they live behind the expand toggle to keep the card to
// one line in the common case. Empty statuses are never shown.
const PRIMARY_STATUSES = ["finished", "queued"];
const SECONDARY_STATUSES = ["started", "abandoned"];

/**
 * One stat in the card: a visual (avatar stack or rating figure) plus a label,
 * with an optional greyed second line under the label (`subLabel`).
 * Desktop lays visual and text side by side; mobile stacks them, left-aligned,
 * so several stats still fit across a phone. The visual slot has a fixed height
 * matching the avatars so labels line up across stats of either kind.
 */
function Stat({ visual, label, subLabel, wrapLabel = false, align = "left" }) {
  const textAlign = { xs: align, sm: "left" };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: {
          xs: align === "right" ? "flex-end" : "flex-start",
          sm: "center",
        },
        gap: { xs: 0.5, sm: 1 },
        minWidth: 0,
      }}
    >
      <Box sx={{ height: AVATAR_SIZE, display: "flex", alignItems: "center" }}>
        {visual}
      </Box>
      {/* Label and subLabel share a column so the pair stacks together even on
          desktop, where the parent lays visual and text out as a row. */}
      <Box sx={{ minWidth: 0, textAlign }}>
        <Typography
          variant="body2"
          noWrap={!wrapLabel}
          sx={{ lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        {subLabel && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", lineHeight: 1.3 }}
          >
            {subLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// The avatar stack shared by the summary card's status buckets and the season
// tiles below the entries, so a person renders identically in both.
function PeopleAvatarGroup({ people, max }) {
  return (
    <AvatarGroup
      max={max}
      sx={{
        "& .MuiAvatar-root": {
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          fontSize: 12,
          borderWidth: 1.5,
        },
      }}
    >
      {people.map((person) => (
        <Avatar
          key={person.id}
          src={person.avatar_url}
          alt={person.username}
          imgProps={{ referrerPolicy: "no-referrer" }}
        />
      ))}
    </AvatarGroup>
  );
}

// Tapping the stack opens a popover listing everyone in this bucket (avatar +
// username, tap through to their profile) — Tooltips don't work on mobile, so
// this is the only way to see who's behind the "+N" overflow avatar there too.
function StatusStat({ statusKey, people, maxAvatars }) {
  const { label } = getStatusOptions()[statusKey];
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`Show who has ${label.toLowerCase()} this`}
        sx={{
          borderRadius: 1,
          justifyContent: "flex-start",
          textAlign: "left",
        }}
      >
        <Stat
          label={
            <>
              <b>{people.length}</b> {label.toLowerCase()}
            </>
          }
          visual={<PeopleAvatarGroup people={people} max={maxAvatars} />}
        />
      </ButtonBase>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        // A touch of breathing room so the popover doesn't sit flush against
        // the bottom edge of the avatars.
        slotProps={{ paper: { sx: { mt: 1 } } }}
      >
        <List dense sx={{ minWidth: 220, maxHeight: 320, overflowY: "auto" }}>
          {people.map((person) => (
            <ListItem key={person.id} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={`/u/${person.username}`}
                onClick={() => setAnchorEl(null)}
              >
                <ListItemAvatar>
                  <Avatar
                    src={person.avatar_url}
                    imgProps={{ referrerPolicy: "no-referrer" }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={person.username}
                  secondary={
                    person.status_at ? getTimeAgo(person.status_at) : null
                  }
                  secondaryTypographyProps={{
                    variant: "caption",
                    color: "text.disabled",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
}

// Default (unsized) Rating, matching how it's shown elsewhere in the app
// (e.g. ActivityJournal, CoverActivity) rather than the compact "small" size.
// Renders as an empty (unfilled) Rating with a plain "No ratings yet" label
// when nobody has rated it yet, rather than disappearing entirely — the
// people bucket above already establishes that someone has tracked this.
// `yourRating` comes from the same backend rule as the followed users' ratings,
// so the two numbers are directly comparable; null when you haven't rated it.
function RatingStat({ average, count, yourRating }) {
  const yourLabel =
    yourRating == null ? null : (
      <>
        yours <b>{yourRating.toFixed(1)}</b>
      </>
    );

  // Count and "yours" share the one grey subLabel line rather than each
  // getting their own, so adding the count doesn't cost the block a third
  // line. Count is omitted once already implied by "No ratings yet" below.
  const countLabel =
    count > 0 ? `${count} rating${count === 1 ? "" : "s"}` : null;
  const subLabel =
    countLabel && yourLabel ? (
      <>
        {countLabel} · {yourLabel}
      </>
    ) : (
      (countLabel ?? yourLabel)
    );

  if (count === 0) {
    return (
      <Stat
        wrapLabel
        align="right"
        label="No ratings yet"
        subLabel={subLabel}
        visual={<Rating value={0} readOnly />}
      />
    );
  }

  return (
    <Stat
      wrapLabel
      align="right"
      label={
        <>
          average <b>{average.toFixed(1)}</b>
        </>
      }
      subLabel={subLabel}
      visual={<Rating value={average} precision={0.1} readOnly />}
    />
  );
}

/**
 * Builds the friend journal-entry data to render below the summary card:
 * everyone's feed-worthy entries on this exact content id. On a TV series tab
 * that's only entries on the series itself — season entries are summarised
 * separately, one tile per season (see SeasonTiles).
 *
 * Returns plain data rather than rendered nodes — sorting/filtering (by date,
 * rating, has-a-note) happens client-side afterwards in ContentFriends, on
 * these same fields, so it needs the raw values rather than pre-built JSX.
 */
function useFriendEntryData(data) {
  return useMemo(
    () =>
      (data?.entries ?? []).map((entry) => ({
        key: `entry-${entry.journal_id}`,
        entry,
        person: entry.author,
        date: entry.date,
        rating: entry.rating,
        hasNote: Boolean(entry.notes?.trim()),
      })),
    [data],
  );
}

// Newest/highest first; entries missing the sorted-by field (an undated entry,
// or one with no rating at all — reactions/notes alone are enough to appear in
// this list) sort last rather than colliding at the front as "0" or "".
function compareByField(field) {
  return (a, b) => {
    if (a[field] == null) return b[field] == null ? 0 : 1;
    if (b[field] == null) return -1;
    return typeof a[field] === "number"
      ? b[field] - a[field]
      : b[field].localeCompare(a[field]);
  };
}

const SORT_OPTIONS = [
  { value: "date", label: "Date", icon: <TodayIcon fontSize="small" /> },
  { value: "rating", label: "Rating", icon: <StarRateIcon fontSize="small" /> },
];

// Combined sort + filter control for the friend entries list — deliberately
// one small button rather than the multi-button toolbar the main Track page
// uses (see features/track/Header.js): this list is usually short, so a
// single "Tune" icon covers both without competing with the summary card
// above it for attention.
function SortFilterButton({
  sortBy,
  onSortByChange,
  notesOnly,
  onNotesOnlyChange,
  minRating,
  onMinRatingChange,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const hasActiveFilter = notesOnly || minRating != null;

  const handleSortSelect = (value) => {
    onSortByChange(value);
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Sort and filter"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Badge color="primary" variant="dot" invisible={!hasActiveFilter}>
          <TuneIcon fontSize="small" />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 230 } } }}
      >
        <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>
          Sort by
        </ListSubheader>
        {SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            dense
            selected={sortBy === option.value}
            onClick={() => handleSortSelect(option.value)}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            {option.label}
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <ListSubheader disableSticky sx={{ lineHeight: "32px" }}>
          Filter
        </ListSubheader>
        {/* Filter controls stay open on interaction (unlike the sort items
            above) so both can be adjusted in one visit to the menu. */}
        <MenuItem dense disableRipple sx={{ cursor: "default" }}>
          <FormControlLabel
            sx={{ ml: 0, width: "100%", justifyContent: "space-between" }}
            labelPlacement="start"
            control={
              <Switch
                size="small"
                checked={notesOnly}
                onChange={(e) => onNotesOnlyChange(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Only with a note</Typography>}
          />
        </MenuItem>
        <MenuItem
          dense
          disableRipple
          sx={{
            cursor: "default",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Minimum rating
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {/* HoverRating rather than a plain Rating — MUI's Rating alone
                misfires on Android touch (see HoverRating.js); no label here
                since this is a filter threshold, not a content rating. */}
            <HoverRating
              value={minRating}
              showLabel={false}
              onChange={(e, newValue) => onMinRatingChange(newValue)}
            />
            {minRating != null && (
              <IconButton
                size="small"
                aria-label="Clear minimum rating"
                onClick={() => onMinRatingChange(null)}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
}

// Under the entries on a TV series tab: one tile per season that followed
// accounts have feed-worthy entries on. Seasons nobody has engaged with are
// left out, so seasons 1 and 5 can sit side by side. The row scrolls sideways
// rather than wrapping, so a long-running show stays one row tall. Tapping a
// tile opens that season's tab, where its entries are listed.
// Every season tile is the same width: exactly three avatars from
// PeopleAvatarGroup, plus the tile's p:1 padding. MUI's AvatarGroup sizes each
// avatar content-box (AVATAR_SIZE plus our 1.5px border on both sides) and
// overlaps them by 8px. Tiles cap their stack at three to match.
const SEASON_TILE_AVATARS = 3;
const seasonTileWidth = (avatarSize) =>
  SEASON_TILE_AVATARS * (avatarSize + 2 * 1.5) -
  (SEASON_TILE_AVATARS - 1) * 8 +
  2 * 8;
const SEASON_TILE_WIDTH = {
  xs: seasonTileWidth(AVATAR_SIZE.xs),
  sm: seasonTileWidth(AVATAR_SIZE.sm),
};

function SeasonTiles({ groups, onSeasonClick }) {
  const peopleCount = new Set(
    groups.flatMap((group) => group.people.map((person) => person.id)),
  ).size;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {`${peopleCount} ${peopleCount === 1 ? "person" : "people"} with season-level entries`}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          overflowX: "auto",
          // Hidden on every device, matching the Explore carousels — the global
          // rule in index.js only hides scrollbars on touch devices.
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {groups.map((group) => (
          <ButtonBase
            key={group.content_id}
            onClick={() => onSeasonClick?.(group.content_id)}
            aria-label={`Open season ${group.season_number}`}
            sx={{
              flexShrink: 0,
              width: SEASON_TILE_WIDTH,
              boxSizing: "border-box",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
              p: 1,
              borderRadius: 1,
              backgroundColor: (theme) => theme.palette.secondary.main,
            }}
          >
            <Typography variant="body2">
              Season {group.season_number}
            </Typography>
            <PeopleAvatarGroup
              people={group.people}
              max={SEASON_TILE_AVATARS}
            />
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

// Under the entries on a season tab: the followed accounts with an entry on the
// whole series. There's only ever this one card, so the count sits beside the
// avatars instead of a heading plus a labelled tile. Rendered plain (no
// background or padding) so it reads as part of the tab rather than a card of
// its own. Tapping it opens the series tab — the full-width tap area on mobile
// makes that easy to hit.
function SeriesTrackingCard({ group, maxAvatars, onClick }) {
  const count = group.people.length;

  return (
    <ButtonBase
      onClick={() => onClick?.(group.content_id)}
      aria-label="Open the series"
      sx={{
        width: { xs: "100%", sm: "fit-content" },
        justifyContent: "flex-start",
        gap: 1,
        borderRadius: 1,
      }}
    >
      <PeopleAvatarGroup people={group.people} max={maxAvatars} />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: "left" }}
      >
        {`${count} ${count === 1 ? "person" : "people"} with series-level entries`}
      </Typography>
    </ButtonBase>
  );
}

/**
 * The "Following" tab of LibraryModal: a summary of how the people the viewer
 * follows have tracked this content, plus their feed-worthy journal entries.
 * Someone else's entry opened directly isn't shown here — LibraryModal gives
 * it a tab of its own instead (see FocusedEntry).
 */
export default function ContentFriends({ contentId, onContentChange }) {
  const { user, login } = useAuth();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [notesOnly, setNotesOnly] = useState(false);
  const [minRating, setMinRating] = useState(null);
  const { data, isLoading } = useFollowingForContent(contentId, {
    enabled: !!user,
  });

  const peopleByStatus = useMemo(() => {
    const buckets = {};
    for (const key of [...PRIMARY_STATUSES, ...SECONDARY_STATUSES]) {
      buckets[key] =
        data?.people?.filter((person) => person.status === key) ?? [];
    }
    return buckets;
  }, [data]);

  const allFriendItems = useFriendEntryData(data);

  // Filtering/sorting is client-side over the already-fetched list — it's
  // small (one content item's worth of followed users), so there's no need
  // to round-trip to the backend for either.
  const friendItems = useMemo(() => {
    let items = allFriendItems;
    if (notesOnly) items = items.filter((item) => item.hasNote);
    if (minRating != null) {
      items = items.filter(
        (item) => item.rating != null && item.rating >= minRating,
      );
    }
    return [...items].sort(compareByField(sortBy));
  }, [allFriendItems, sortBy, notesOnly, minRating]);

  if (!user) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.disabled">
          <MuiLink component="span" onClick={login} sx={{ cursor: "pointer" }}>
            Sign in
          </MuiLink>{" "}
          to see how the people you follow are engaging with this.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Stack direction="column" spacing={1}>
          <Skeleton width="70%" />
          <Skeleton width="60%" />
        </Stack>
      </Box>
    );
  }

  if (!data?.following_count) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.disabled">
          Follow people to see who's queued, started and finished this.
        </Typography>
      </Box>
    );
  }

  // season_number is only set when this content is a TV season.
  const isSeason = data.season_number != null;

  if (!data.people.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.disabled">
          {isSeason
            ? "None of the people you follow have tracked this season yet."
            : "None of the people you follow have tracked this yet."}
        </Typography>
      </Box>
    );
  }

  const nonEmpty = (keys) => keys.filter((key) => peopleByStatus[key].length);
  const primaryToShow = nonEmpty(PRIMARY_STATUSES);
  const secondaryToShow = nonEmpty(SECONDARY_STATUSES);

  // Queued/finished always take the top line when either exists. Only when
  // both are empty do started/stopped fill that space instead — regardless of
  // whether there's a rating to show alongside them, so the line is never left
  // empty with content sitting unseen behind the toggle.
  const promoteSecondary = primaryToShow.length === 0;
  const topStatuses = promoteSecondary ? secondaryToShow : primaryToShow;
  const hasToggle = !promoteSecondary && secondaryToShow.length > 0;

  // Three avatars plus the "+N" overflow is the most a phone-width stat fits.
  // On desktop, drop to the same cap whenever the toggle button is present —
  // its reserved space (see pr below) leaves less room for the top row, and
  // two people's worth of stacks (queued + finished) can otherwise overflow.
  const maxAvatars = isMobile || hasToggle ? 3 : 4;

  const renderStatuses = (keys) =>
    keys.map((key) => (
      <StatusStat
        key={key}
        statusKey={key}
        people={peopleByStatus[key]}
        maxAvatars={maxAvatars}
      />
    ));

  // season_groups is null except on a TV series tab (see
  // _get_following_entries), where it's a list — possibly empty.
  const isSeries = data.season_groups != null;
  // Links to the rest of the show under the entries: season tiles on a series
  // tab, the parent series card on a season tab (see series_group).
  const hasShowLinks = isSeries
    ? data.season_groups.length > 0
    : Boolean(data.series_group);

  return (
    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          position: "relative",
          backgroundColor: (theme) => theme.palette.secondary.main,
          borderRadius: 1,
          p: 1.5,
          // Leave room for the expand button so the rating never sits under it.
          pr: hasToggle ? 5 : 1.5,
        }}
      >
        {/* Counts cluster left; the rating is pushed to the right edge. */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {renderStatuses(topStatuses)}
          </Box>
          <Box sx={{ ml: "auto", flexShrink: 0 }}>
            <RatingStat
              average={data.rating.average}
              count={data.rating.count}
              yourRating={data.your_rating}
            />
          </Box>
        </Box>

        {hasToggle && (
          <>
            <IconButton
              size="small"
              aria-label={
                expanded ? "Show fewer statuses" : "Show more statuses"
              }
              onClick={() => setExpanded((prev) => !prev)}
              sx={{ position: "absolute", top: 4, right: 4 }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: (theme) =>
                    theme.transitions.create("transform", {
                      duration: theme.transitions.duration.shortest,
                    }),
                }}
              />
            </IconButton>
            <Collapse in={expanded}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
                {renderStatuses(secondaryToShow)}
              </Box>
            </Collapse>
          </>
        )}
      </Box>

      {allFriendItems.length === 0 ? (
        // On a TV series or season tab the tiles below can be the only
        // content — no "nothing yet" message when they're about to show.
        !hasShowLinks && (
          <Typography variant="body2" color="text.disabled">
            No journal entries from people you follow yet
          </Typography>
        )
      ) : (
        <>
          {/* Thin header row above the list — count reflects the active
              filter, not the unfiltered total, so it visibly changes as
              filters are applied rather than just the list beneath it. */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {friendItems.length} journal{" "}
              {friendItems.length === 1 ? "entry" : "entries"}
              {/* On a TV show, say which level these entries are for — on a
                  series tab the season entries are summarised in tiles below. */}
              {isSeries && " for series"}
              {isSeason && ` for season ${data.season_number}`}
            </Typography>
            <Box sx={{ ml: "auto" }}>
              <SortFilterButton
                sortBy={sortBy}
                onSortByChange={setSortBy}
                notesOnly={notesOnly}
                onNotesOnlyChange={setNotesOnly}
                minRating={minRating}
                onMinRatingChange={setMinRating}
              />
            </Box>
          </Box>

          {friendItems.length === 0 ? (
            <Typography variant="body2" color="text.disabled">
              No entries match this filter.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {friendItems.map((item) => (
                <motion.div key={item.key} layout="position">
                  <FriendJournalEntry entry={item.entry} person={item.person} />
                </motion.div>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Outside the sort/filter above: these tiles are navigation to other
          tabs of the show, not entries the filters apply to. */}
      {isSeries && hasShowLinks && (
        <SeasonTiles
          groups={data.season_groups}
          onSeasonClick={onContentChange}
        />
      )}
      {!isSeries && hasShowLinks && (
        <SeriesTrackingCard
          group={data.series_group}
          maxAvatars={maxAvatars}
          onClick={onContentChange}
        />
      )}
    </Box>
  );
}
